import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';

import { NO_FAVORITES_MESSAGE } from 'constants/siteMessages';
import {
  evAddToCart,
  evProductClickthrough,
  evRemoveFromFavorites,
  pvFavorites
} from 'events/favorites';

import { trackEvent } from 'helpers/analytics';
import { offset } from 'helpers/ClientUtils';
import { pageTypeChange } from 'actions/common';
import { addHeartToCart, fetchHeartList, unHeartProduct } from 'actions/hearts';
import { titaniteView, track } from 'apis/amethyst';
import { firePixelServer } from 'actions/pixelServer';
import AccountHeader from 'containers/account/AccountHeader';
import FavoriteItemProduct from 'components/account/Favorites/FavoriteItemProduct';
import FavoriteItemActions from 'components/account/Favorites/FavoriteItemActions';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import { onEvent } from 'helpers/EventHelpers';
import css from 'styles/containers/account/favorites.scss';

const LAZYLOAD_HEARTS_OFFSET = 1000;

export class Favorites extends Component {
  static fetchDataOnServer(store) {
    return store.dispatch(fetchHeartList());
  }

  constructor(props) {
    super(props);
    this.onScrollPage = throttle(this.onScrollPage.bind(this), 100);
  }

  state = {
    oosItems: []
  }

  componentDidMount() {
    titaniteView();
    const { firePixelServer, pageTypeChange } = this.props;

    pageTypeChange('favorites');
    this.fetchData();

    trackEvent('TE_PV_FAVORITES');
    firePixelServer('favorites');
  }

  onScrollPage() {
    const { scrollY, pageYOffset, innerHeight } = window;
    const { loadMore } = this.refs;

    if (!loadMore) {
      return this.scrollHandler.unbind();
    }

    const { fetchHeartList, hearts: { nextPageToken } } = this.props;
    const offsetScroll = (scrollY || pageYOffset) + innerHeight + LAZYLOAD_HEARTS_OFFSET;
    if (offsetScroll > offset(loadMore).top) {
      // unbind scroll while we fetch more hearts
      this.scrollHandler.unbind();
      fetchHeartList({ nextPageToken, concat: true }).then(() => {
        // rebind scroll handler after more hearts have been received
        if (loadMore) {
          this.setupScrollHandler();
        }
      });
    }
  }

  setupScrollHandler = () => {
    this.scrollHandler = onEvent(window, 'scroll', this.onScrollPage, null, this);
  }

  fetchData() {
    const { fetchHeartList, hearts: { hearts } } = this.props;

    if (!hearts) {
      fetchHeartList().then(({ items }) => {
        track(pvFavorites, items);
        this.setupScrollHandler();
      });
    } else {
      track(pvFavorites, hearts);
      this.setupScrollHandler();
    }
  }

  handleHeartAddToCart = (item, event, win = window) => {
    win.scrollTo(0, 0);
    const { addHeartToCart } = this.props;
    const { asin, stockId } = item;
    const id = asin || stockId;

    track(evAddToCart, item);
    addHeartToCart(id);
  };

  handleHeartRemove = item => {
    const { unHeartProduct } = this.props;
    const { styleId: itemId, asin: subItemId } = item;

    track(evRemoveFromFavorites, item);
    trackEvent('TE_FAVORITES_DELETE');
    unHeartProduct({ itemId, subItemId });
  }

  handleProductClick = item => {
    track(evProductClickthrough, item);
    trackEvent('TE_FAVORITES_PDPCLICK');
  }

  render() {
    const {
      hearts: { hearts, nextPageToken, oosHearts }
    } = this.props;

    const { testId } = this.context;

    if (!hearts) {
      return <PageLoader centered={true}/>;
    }

    return (
      <SiteAwareMetadata>
        <div className={css.favorites}>
          <div className={css.accountHeader}>
            <AccountHeader title="Your Favorites" showFirstName={true} showCustomerEmail={false}/>
          </div>

          <div>
            <div className={css.header}>
              <span>Items</span>
              <span>Price</span>
            </div>
            {hearts.length ? hearts.map(item => {
              const { itemId } = item;

              return (
                <div key={itemId} className={css.listItem} data-test-id={testId('favoriteItem')}>
                  <FavoriteItemProduct oosHearts={oosHearts} onProductClick={this.handleProductClick.bind(this, item)} item={item}/>
                  <FavoriteItemActions
                    item={item}
                    onAddToCart={this.handleHeartAddToCart}
                    onRemove={this.handleHeartRemove}/>
                </div>
              );
            }) : <div className={css.listItem} data-test-id={testId('noFavorites')}>{NO_FAVORITES_MESSAGE}</div>}
          </div>

          { !!nextPageToken &&
            <button
              type="button"
              aria-hidden="true"
              aria-live="polite"
              className={css.loadMore}
              ref="loadMore">
              More
            </button>
          }
        </div>
      </SiteAwareMetadata>
    );
  }
}

Favorites.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func
};

const mapStateToProps = ({ hearts }) => ({
  hearts
});

export default connect(mapStateToProps,{
  addHeartToCart,
  fetchHeartList,
  firePixelServer,
  pageTypeChange,
  unHeartProduct
})(Favorites);
